/**
 * Risk From layer: one fixed class ramp, two metrics, four geometries.
 *
 * Classes are fixed per metric (not stretched to the run's min/max) so a
 * treated strategy and its No Mitigation baseline read on the same scale.
 * Lightness falls as risk rises, so the classes separate without relying on
 * hue alone.
 */

import type { UnitSystem } from "../../utils/units";
import { acresToHectares, formatArea, formatNumber } from "../../utils/units";

export type RiskFromMetric = "fire_size" | "impacted_assets";

export const RISK_FROM_CLASS_COUNT = 5;

/** Class colours, lowest risk first. */
export const RISK_FROM_RAMP = ["#FEF08A", "#FACC15", "#F97316", "#EF4444", "#B91C1C"] as const;

/** Fill alpha for grid cells, by class. Low classes stay a light veil so the imagery reads through. */
export const RISK_FROM_CELL_ALPHA = [0.24, 0.4, 0.54, 0.66, 0.8] as const;

/** Point radius (px) by class — size and colour agree. */
export const RISK_FROM_POINT_RADIUS = [7, 9, 11, 13, 15] as const;

/** Polygon ignitions: fill alpha at rest and under the pointer. */
export const RISK_FROM_SHAPE_ALPHA = 0.38;
export const RISK_FROM_SHAPE_ALPHA_HOVER = 0.55;

/** Everything except the loaded ignition drops to this while the fire plays. */
export const RISK_FROM_PLAYING_OPACITY = 0.4;

export const RISK_FROM_ACCENT = "#60D1AF";
export const RISK_FROM_CASING = "rgba(8, 12, 20, 0.6)";

export interface RiskFromMetricDef {
  key: RiskFromMetric;
  label: string;
  icon: string;
  /** Upper bounds of classes 1–4, in the metric's native unit (ha / count). */
  breaks: number[];
  /** Nominal top of the last class, for placing a value on the ramp. */
  max: number;
  /** Tick labels under the ramp, one per class. */
  ticks: string[];
  format: (value: number) => string;
}

const FIRE_SIZE_BREAKS_HA = [50, 100, 200, 400];
const FIRE_SIZE_BREAKS_ACRES = [125, 250, 500, 1000];
const ASSET_BREAKS = [5, 20, 50, 100];

/** Metric definitions, with fire-size classes in the user's unit system. */
export const riskFromMetric = (
  metric: RiskFromMetric,
  unitSystem: UnitSystem
): RiskFromMetricDef => {
  if (metric === "impacted_assets") {
    return {
      key: metric,
      label: "Assets impacted",
      icon: "home",
      breaks: ASSET_BREAKS,
      max: 250,
      ticks: ["0", "5", "20", "50", "100+"],
      format: (v) =>
        `${Number.isInteger(v) ? v : v.toFixed(1)} ${v === 1 ? "asset" : "assets"}`,
    };
  }
  const imperial = unitSystem === "imperial";
  const displayBreaks = imperial ? FIRE_SIZE_BREAKS_ACRES : FIRE_SIZE_BREAKS_HA;
  return {
    key: metric,
    label: "Fire size",
    icon: "local_fire_department",
    breaks: imperial ? FIRE_SIZE_BREAKS_ACRES.map(acresToHectares) : FIRE_SIZE_BREAKS_HA,
    max: imperial ? acresToHectares(2000) : 800,
    ticks: ["0", ...displayBreaks.slice(0, 3).map((b) => formatNumber(b)), `${formatNumber(displayBreaks[3])}+`],
    format: (v) => formatArea(v, unitSystem),
  };
};

/** Class index 0–4 for a value. */
export const riskFromClass = (value: number, def: RiskFromMetricDef): number => {
  let i = 0;
  while (i < def.breaks.length && value >= def.breaks[i]) i++;
  return i;
};

/** Where a value sits along the ramp, 0–100 (%), linear inside its class. */
export const riskFromRampPosition = (value: number, def: RiskFromMetricDef): number => {
  const i = riskFromClass(value, def);
  const lo = i === 0 ? 0 : def.breaks[i - 1];
  const hi = i === def.breaks.length ? def.max : def.breaks[i];
  const within = hi > lo ? Math.min(1, Math.max(0, (value - lo) / (hi - lo))) : 0;
  return Math.round(((i + within) / RISK_FROM_CLASS_COUNT) * 1000) / 10;
};

export const hexToRgba = (hex: string, alpha: number): string => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/**
 * Feature kinds the layer draws. `chip` is the one-point label beside the
 * loaded ignition; the others are the ignitions themselves.
 */
export type RiskFromFeatureKind = "cell" | "point" | "shape" | "line" | "chip";

const KIND = ["get", "kind"];
const isKind = (...kinds: RiskFromFeatureKind[]) =>
  kinds.length === 1 ? ["==", KIND, kinds[0]] : ["in", KIND, ["literal", kinds]];
const SELECTED = ["==", ["get", "isSelected"], true];
// Hover is feature-state, set straight on the map by the layer's onHover, so
// moving across cells never rebuilds the GeoJSON or re-renders React.
const HOVERED = ["boolean", ["feature-state", "hover"], false];
const HIDDEN = ["==", ["get", "hidden"], true];
const DIMMED = ["==", ["get", "dimmed"], true];
/** 0 when the class is filtered out, reduced while the fire plays, else full. */
const visibility = (full: number) => ["case", HIDDEN, 0, DIMMED, full * RISK_FROM_PLAYING_OPACITY, full];

/** Dash pattern for the "loaded" outline (in line widths). */
const LOADED_DASH = [4, 3];

/** Mapbox layer styles for one Risk From layer, in draw order. */
export const riskFromLayerStyles = (id: string) => {
  const fill = {
    id,
    type: "fill",
    filter: isKind("cell", "shape"),
    paint: {
      // Polygon ignitions brighten under the pointer (fillHover); cells keep
      // their class alpha and take the white outline instead.
      "fill-color": ["case", HOVERED, ["coalesce", ["get", "fillHover"], ["get", "fill"]], ["get", "fill"]],
      "fill-opacity": visibility(1),
    },
  };

  // Dark casing under polygon strokes and line ignitions so they hold up
  // over bright imagery.
  const casing = {
    id: `${id}-casing`,
    type: "line",
    filter: isKind("shape", "line"),
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": RISK_FROM_CASING,
      "line-width": ["case", isKind("line"), 7, 5.5],
      "line-opacity": visibility(1),
    },
  };

  const lines = {
    id: `${id}-lines`,
    type: "line",
    filter: isKind("line"),
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": ["get", "color"],
      "line-width": 4,
      "line-opacity": visibility(1),
    },
  };

  // Cells: no stroke at rest, white when hovered or loaded. Polygons: a
  // class-colour stroke at rest.
  const outline = {
    id: `${id}-outline`,
    type: "line",
    filter: isKind("cell", "shape"),
    layout: { "line-join": "round" },
    paint: {
      "line-color": [
        "case",
        ["any", SELECTED, HOVERED],
        "#ffffff",
        isKind("shape"),
        ["get", "color"],
        "rgba(0,0,0,0)",
      ],
      "line-width": [
        "case",
        ["any", SELECTED, HOVERED],
        2,
        isKind("shape"),
        2.5,
        0,
      ],
      "line-opacity": visibility(1),
    },
  };

  // Loaded polygon / line: a fine white dashed line on top.
  const loadedDash = {
    id: `${id}-loaded-dash`,
    type: "line",
    filter: ["all", isKind("shape", "line"), SELECTED],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#ffffff",
      "line-width": 1.25,
      "line-dasharray": LOADED_DASH,
    },
  };

  // Points: dark casing (a white ring when loaded) under a class-coloured
  // disc with a white stroke; the disc grows with the class.
  const radius = ["get", "radius"];
  const pointCasing = {
    id: `${id}-point-casing`,
    type: "circle",
    filter: isKind("point"),
    paint: {
      "circle-radius": ["case", SELECTED, ["+", radius, 6], HOVERED, ["+", radius, 5], ["+", radius, 3]],
      "circle-color": ["case", SELECTED, "#ffffff", HOVERED, "rgba(255,255,255,0.28)", "rgba(10, 14, 22, 0.55)"],
      "circle-opacity": visibility(1),
    },
  };
  const points = {
    id: `${id}-points`,
    type: "circle",
    filter: isKind("point"),
    paint: {
      "circle-radius": ["case", HOVERED, ["*", radius, 1.15], radius],
      "circle-color": ["get", "color"],
      "circle-opacity": visibility(1),
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ffffff",
      "circle-stroke-opacity": visibility(1),
    },
  };

  // "Loaded · 620 ha" beside the loaded ignition.
  const chip = {
    id: `${id}-chip`,
    type: "symbol",
    filter: isKind("chip"),
    layout: {
      "text-field": [
        "format",
        ["get", "chipState"],
        { "text-color": RISK_FROM_ACCENT },
        "  ·  ",
        { "text-color": "#94A3B8" },
        ["get", "chipValue"],
        { "text-color": "#F1F5F9" },
      ],
      "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
      "text-size": 12,
      "text-anchor": "left",
      // Radial (a single data-driven number): text-offset's array form
      // cannot be read from a feature property.
      "text-radial-offset": ["get", "chipOffset"],
      "text-justify": "left",
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: {
      "text-halo-color": "rgba(17, 22, 33, 0.92)",
      "text-halo-width": 1.6,
      "text-halo-blur": 0.4,
    },
  };

  return [fill, casing, lines, outline, loadedDash, pointCasing, points, chip];
};

/** Layer ids that take pointer events, for the given base id. */
export const riskFromInteractiveLayerIds = (id: string) => [
  id,
  `${id}-outline`,
  `${id}-lines`,
  `${id}-points`,
];
